import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

const baseQuery = fetchBaseQuery({
  baseUrl: `${process.env.REACT_APP_API_URL}/api`,
  prepareHeaders: (headers, { getState }) => {
    const token = getState().auth.token;
    if (token) {
      headers.set('authorization', `Bearer ${token}`);
    }
    return headers;
  },
});

export const apiSlice = createApi({
  baseQuery,
  tagTypes: ['TourPackage', 'Auth', 'Inclusion', 'Tag', 'Gallery', 'Inquiry', 'Lead', 'CustomTour', 'StaticContent'],
  endpoints: (builder) => ({
    // Auth Mutation
    login: builder.mutation({
      query: (credentials) => ({
        url: '/auth/login',
        method: 'POST',
        body: credentials,
      }),
    }),

    // Tour Package Mutations
    getTourPackages: builder.mutation({
      query: (params = {}) => ({ 
        url: '/tour-packages', 
        method: 'GET',
        params 
      }),
      providesTags: ['TourPackage'],
    }),
    getTourPackageById: builder.mutation({
      query: (id) => ({ url: `/tour-packages/${id}`, method: 'GET' }),
    }),
    createTourPackage: builder.mutation({
      query: (data) => ({ url: '/tour-packages', method: 'POST', body: data }),
      invalidatesTags: ['TourPackage'],
    }),
    updateTourPackage: builder.mutation({
      query: ({ id, ...data }) => ({ url: `/tour-packages/${id}`, method: 'PUT', body: data }),
      invalidatesTags: ['TourPackage'],
    }),
    deleteTourPackage: builder.mutation({
      query: (id) => ({ url: `/tour-packages/${id}`, method: 'DELETE' }),
      invalidatesTags: ['TourPackage'],
    }),

    // Inclusion Mutations
    getInclusions: builder.mutation({
      query: () => ({ url: '/inclusions', method: 'GET' }),
      providesTags: ['Inclusion'],
    }),
    createInclusion: builder.mutation({
      query: (data) => ({ url: '/inclusions', method: 'POST', body: data }),
      invalidatesTags: ['Inclusion'],
    }),
    deleteInclusion: builder.mutation({
      query: (id) => ({ url: `/inclusions/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Inclusion'],
    }),

    // Tag Mutations
    getTags: builder.mutation({
      query: () => ({ url: '/tags', method: 'GET' }),
      providesTags: ['Tag'],
    }),
    createTag: builder.mutation({
      query: (data) => ({ url: '/tags', method: 'POST', body: data }),
      invalidatesTags: ['Tag'],
    }),
    deleteTag: builder.mutation({
      query: (id) => ({ url: `/tags/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Tag'],
    }),

    // Gallery Mutations
    getGallery: builder.mutation({
      query: (params = {}) => ({ url: '/gallery', method: 'GET', params }),
      providesTags: ['Gallery'],
    }),
    createGallery: builder.mutation({
      query: (data) => ({ url: '/gallery', method: 'POST', body: data }),
      invalidatesTags: ['Gallery'],
    }),
    deleteGallery: builder.mutation({
      query: (id) => ({ url: `/gallery/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Gallery'],
    }),

    // Inquiry Mutations
    getInquiries: builder.mutation({
      query: (params = {}) => ({ url: '/inquiries', method: 'GET', params }),
      providesTags: ['Inquiry'],
    }),
    deleteInquiry: builder.mutation({
      query: (id) => ({ url: `/inquiries/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Inquiry'],
    }),

    // Lead Mutations
    getLeads: builder.mutation({
      query: (params = {}) => ({ url: '/leads', method: 'GET', params }),
      providesTags: ['Lead'],
    }),
    deleteLead: builder.mutation({
      query: (id) => ({ url: `/leads/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Lead'],
    }),

    // Custom Tour Mutations
    getCustomTours: builder.mutation({
      query: (params = {}) => ({ url: '/custom-tours', method: 'GET', params }),
      providesTags: ['CustomTour'],
    }),
    deleteCustomTour: builder.mutation({
      query: (id) => ({ url: `/custom-tours/${id}`, method: 'DELETE' }),
      invalidatesTags: ['CustomTour'],
    }),
    // Upload Mutations
    uploadImages: builder.mutation({
      query: (formData) => ({
        url: '/upload',
        method: 'POST',
        body: formData,
      }),
    }),
    deleteImage: builder.mutation({
      query: (filename) => ({
        url: `/upload/${filename}`,
        method: 'DELETE',
      }),
    }),
    // Static Content Mutations
    getStaticContent: builder.mutation({
      query: () => ({ url: '/static-content', method: 'GET' }),
      providesTags: ['StaticContent'],
    }),
    updateStaticContent: builder.mutation({
      query: (data) => ({ url: '/static-content', method: 'PUT', body: data }),
      invalidatesTags: ['StaticContent'],
    }),
  }),
});

export const {
  useLoginMutation,
  useGetTourPackagesMutation,
  useGetTourPackageByIdMutation,
  useCreateTourPackageMutation,
  useUpdateTourPackageMutation,
  useDeleteTourPackageMutation,
  useGetInclusionsMutation,
  useCreateInclusionMutation,
  useDeleteInclusionMutation,
  useGetTagsMutation,
  useCreateTagMutation,
  useDeleteTagMutation,
  useGetGalleryMutation,
  useCreateGalleryMutation,
  useDeleteGalleryMutation,
  useGetInquiriesMutation,
  useDeleteInquiryMutation,
  useGetLeadsMutation,
  useDeleteLeadMutation,
  useGetCustomToursMutation,
  useDeleteCustomTourMutation,
  useUploadImagesMutation,
  useDeleteImageMutation,
  useGetStaticContentMutation,
  useUpdateStaticContentMutation,
} = apiSlice;
